// useInstallPrompt — Day 13 PR-C (D11+D12): detect whether the user is in
// Safari tab / Android Chrome on mobile and needs to add the PWA to home
// screen.
//
// Rationale: iOS Safari tab and PWA standalone have isolated localStorage.
// If a learner logs in via Safari then adds to home screen, the PWA can't
// see the saved userId and forces a re-login. Instead, we show a full-
// screen "please add to home screen" overlay BEFORE the first login, so
// the learner installs first and logs in inside the PWA only.
//
// Bypass conditions (returns "not_needed"):
// - Already standalone (display-mode: standalone, or navigator.standalone)
// - Desktop user-agent (Joe / reviewers on Mac/Windows just browsing)
// - `?preview=true` query param (workers QA'ing learner UI)

import { useEffect, useState } from "react";

export type InstallState =
  | "not_needed"
  | "ios_add"
  | "android_native"
  | "android_manual";

export interface InstallPromptState {
  state: InstallState;
  /** Trigger native Android install prompt; undefined if not available. */
  triggerNative?: () => Promise<void>;
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function detectInitial(): InstallState {
  if (typeof window === "undefined") return "not_needed";

  // Bypass: ?preview=true (sticky via sessionStorage) — workers QA'ing learner UI
  const previewFromQuery =
    new URLSearchParams(window.location.search).get("preview") === "true";
  const previewSticky =
    typeof window !== "undefined" &&
    window.sessionStorage.getItem("goodbye:preview") === "true";
  if (previewFromQuery || previewSticky) return "not_needed";

  // Day 14: ?simulate=pin also bypasses InstallPrompt. Joe tests "我找到了"
  // from Taipei (30km from any pin) — needs to run in Safari tab so the
  // query string survives.
  // Persist the flag here too (not just in MapView) because the first
  // route after detection is usually NameLogin, not MapView, so MapView's
  // own sessionStorage write wouldn't fire until later.
  const simulateQuery = new URLSearchParams(window.location.search).get("simulate");
  if (simulateQuery === "pin") {
    try {
      window.sessionStorage.setItem("goodbye:simulate", "pin");
    } catch {}
    return "not_needed";
  }
  if (simulateQuery === "clear") {
    try {
      window.sessionStorage.removeItem("goodbye:simulate");
    } catch {}
  }
  const simulateSticky =
    window.sessionStorage.getItem("goodbye:simulate") === "pin";
  if (simulateSticky) return "not_needed";

  const ua = navigator.userAgent;
  const isMobile = /iPhone|iPad|iPod|Android/i.test(ua);
  if (!isMobile) return "not_needed";

  // Already PWA standalone? Don't show prompt.
  if ((navigator as { standalone?: boolean }).standalone === true) return "not_needed";
  if (window.matchMedia("(display-mode: standalone)").matches) return "not_needed";

  if (/iPhone|iPad|iPod/i.test(ua)) return "ios_add";
  return "android_manual"; // will upgrade to android_native if beforeinstallprompt fires
}

export function useInstallPrompt(): InstallPromptState {
  const [state, setState] = useState<InstallState>(() => detectInitial());
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (state === "not_needed") return;

    function onBeforeInstallPrompt(e: Event) {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setState("android_native");
    }
    function onAppInstalled() {
      setDeferred(null);
      setState("not_needed");
    }
    function onDisplayChange() {
      // PWA was just installed and switched display-mode
      if (window.matchMedia("(display-mode: standalone)").matches) {
        setState("not_needed");
      }
    }

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);
    const mq = window.matchMedia("(display-mode: standalone)");
    mq.addEventListener?.("change", onDisplayChange);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
      mq.removeEventListener?.("change", onDisplayChange);
    };
  }, [state]);

  const triggerNative = deferred
    ? async () => {
        await deferred.prompt();
        const choice = await deferred.userChoice;
        if (choice.outcome === "accepted") {
          setDeferred(null);
          // setState happens via appinstalled event listener
        }
      }
    : undefined;

  return { state, triggerNative };
}
